import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/Button';

const resetSchema = z.object({
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
}).refine((d) => d.password === d.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type ResetFormData = z.infer<typeof resetSchema>;

const inputClasses =
  'w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-slate-700 placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none';

const labelClasses = 'block text-sm font-bold text-slate-700 mb-1.5';

const errorClasses = 'text-xs text-error mt-1';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { confirmPasswordReset } = useAuth();
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
  });

  const onSubmit = async (data: ResetFormData) => {
    if (!token) return;
    setServerError(null);
    try {
      await confirmPasswordReset({ token, newPassword: data.password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to reset password. The link may have expired.';
      setServerError(message);
    }
  };

  return (
    <div className="bg-background">
      {/* ===== SECTION 1: PAGE HERO ===== */}
      <section className="relative py-24 lg:py-32 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-15" style={{ backgroundImage: "url('/images/goldson-wade-2018-hero.jpg')" }} />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800/90 to-primary-dark/85" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 border border-primary/30 mb-6">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span className="text-xs font-bold tracking-widest uppercase text-white">Account Recovery</span>
          </span>
          <h1 className="heading-hero text-white mb-6 max-w-4xl mx-auto">Set New Password</h1>
          <p className="text-lg text-slate-200 leading-relaxed max-w-2xl mx-auto">
            Choose a new password for your account.
          </p>
        </div>
      </section>

      {/* ===== SECTION 2: FORM ===== */}
      <section className="py-24 bg-white">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
          {!token ? (
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-slate-900">Invalid Reset Link</h2>
              <p className="text-slate-600 text-sm leading-relaxed">
                This password reset link is invalid or missing. Please request a new one.
              </p>
              <Link
                to="/forgot-password"
                className="inline-block mt-4 text-primary font-bold hover:underline text-sm"
              >
                Request New Reset Link
              </Link>
            </div>
          ) : success ? (
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-slate-900">Password Reset Successfully</h2>
              <p className="text-slate-600 text-sm leading-relaxed">
                Your password has been updated. Redirecting to login...
              </p>
              <Link
                to="/login"
                className="inline-block mt-4 text-primary font-bold hover:underline text-sm"
              >
                Go to Login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
              {serverError && (
                <div className="rounded-lg bg-error/10 border border-error/20 p-4 text-sm text-error">
                  {serverError}
                </div>
              )}

              <div>
                <label htmlFor="reset-password" className={labelClasses}>New Password</label>
                <input
                  id="reset-password"
                  type="password"
                  placeholder="At least 8 characters"
                  className={inputClasses}
                  {...register('password')}
                />
                {errors.password && <p className={errorClasses}>{errors.password.message}</p>}
              </div>

              <div>
                <label htmlFor="reset-confirm" className={labelClasses}>Confirm Password</label>
                <input
                  id="reset-confirm"
                  type="password"
                  placeholder="Re-enter your password"
                  className={inputClasses}
                  {...register('confirmPassword')}
                />
                {errors.confirmPassword && <p className={errorClasses}>{errors.confirmPassword.message}</p>}
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Resetting...' : 'Reset Password'}
              </Button>

              <p className="text-center text-sm text-slate-600">
                Remember your password?{' '}
                <Link to="/login" className="text-primary font-bold hover:underline">
                  Log in
                </Link>
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
